import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { CalendarClock, Loader2, Mail } from 'lucide-react';
import { cn } from '../../../shared';
import {
  fetchTimeSuggestions,
  markTimeSuggestionHandled,
  type SuggestionCheck,
  type TimeSuggestion,
} from '../services/interviewsApi';
import { isMine } from './logScope';

const QUERY_KEY = ['time-suggestions'];

/** What the interviewers' calendars say, in words a recruiter can act on. */
export function checkLabel(s: TimeSuggestion): { text: string; tone: 'ok' | 'bad' | 'muted' } {
  if (s.past || !s.check) return { text: 'This time has already passed.', tone: 'muted' };
  const c: SuggestionCheck = s.check;
  const panel = s.meetingType === 'panel';
  if (c.status === 'free') {
    return {
      text: panel
        ? 'Free: the whole panel is free at once in that range.'
        : c.total > 1
          ? `Free: ${c.freeCount ?? 0} of ${c.total} interviewers can take it.`
          : 'Free: the interviewer can take it.',
      tone: 'ok',
    };
  }
  if (c.status === 'clash') {
    return {
      text: panel
        ? 'Clash: the panel is not free at once for the whole interview in that range.'
        : 'Clash: no interviewer is free for the whole interview in that range.',
      tone: 'bad',
    };
  }
  return { text: 'A calendar could not be read, so this time could not be checked.', tone: 'muted' };
}

/**
 * Times candidates suggested themselves ("None of these work? Suggest a time"). Nothing is booked:
 * the recruiter replies to the candidate, agrees a time the usual way and marks the card handled.
 * Shown only while there is something open, so the Log is unchanged for everyone else.
 */
export function TimeSuggestionsPanel({ mine }: { mine: string | null }) {
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchTimeSuggestions,
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      (data ?? []).filter(
        (s) => !mine || isMine(s.owner, mine) || s.interviewers.some((e) => isMine(e, mine))
      ),
    [data, mine]
  );

  async function handled(id: string) {
    setBusyId(id);
    setFailed(null);
    try {
      await markTimeSuggestionHandled(id);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } catch {
      setFailed(id);
    } finally {
      setBusyId(null);
    }
  }

  if (isPending) return null;
  if (error) {
    return (
      <p className="mb-5 text-xs text-red-700">
        Could not load the times candidates suggested.{' '}
        <button type="button" className="underline" onClick={() => void refetch()}>
          Try again
        </button>
      </p>
    );
  }
  if (rows.length === 0) return null;

  return (
    <section className="mb-6" aria-label="Times candidates suggested">
      <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <CalendarClock className="h-4 w-4" />
        Candidates suggested a time ({rows.length})
      </h2>
      <p className="mb-2 max-w-2xl text-xs text-gray-500">
        None of the offered times worked for these candidates. Nothing is booked — reply to them, agree
        a time, then mark it as handled.
      </p>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {rows.map((s, idx) => {
          const check = checkLabel(s);
          return (
            <div
              key={s.id}
              className={cn('flex flex-wrap items-start gap-3 p-3.5', idx > 0 && 'border-t border-gray-100')}
            >
              <div className="min-w-[220px] flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {s.candidateName || s.candidateEmail || 'Unknown candidate'}
                </p>
                <p className="mt-0.5 text-xs text-gray-600">
                  {[s.stageName, s.jobTitle].filter(Boolean).join(' · ')}
                  {s.minutes ? ` · ${s.minutes} min` : ''}
                </p>
                <p className="mt-1 text-sm text-gray-900">Suggested: {s.when}</p>
                <p
                  className={cn(
                    'mt-0.5 text-xs font-medium',
                    check.tone === 'ok' && 'text-green-700',
                    check.tone === 'bad' && 'text-[#854F0B]',
                    check.tone === 'muted' && 'text-gray-500'
                  )}
                >
                  {check.text}
                </p>
                {s.note ? <p className="mt-1 whitespace-pre-line text-xs italic text-gray-600">“{s.note}”</p> : null}
                {failed === s.id ? (
                  <p className="mt-1 text-xs text-red-700">Could not mark it as handled. Please try again.</p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                {s.candidateEmail ? (
                  <a
                    href={`mailto:${s.candidateEmail}?subject=${encodeURIComponent(
                      `Your interview${s.jobTitle ? ` — ${s.jobTitle}` : ''}`
                    )}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:border-gray-500"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Reply
                  </a>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === s.id}
                  onClick={() => void handled(s.id)}
                >
                  {busyId === s.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                  Mark as handled
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
